// useHqEvents — cross-room event bus for the HQ.
//
// Any room that mutates data fires an `hq:<kind>` window event; any
// room that shows aggregated counts subscribes to it and refetches.
// This is the minimum viable cross-room sync — realtime Supabase
// subscriptions ship in a follow-up when we have channels configured.
//
// Event contract (kinds we've standardized so far):
//   hq:lead-stage-change    { leadId, stage }
//   hq:appointment-updated  { apptId, status }
//   hq:consent-changed      { entity, action }
//   hq:statement-imported   { statementId }
//   hq:audit-event          { category }
//
// Emitters: PipelineRoom (stage), CalendarRoom (appt), RevenueVaultRoom
// (import), ComplianceVaultRoom (audit).
//
// Subscribers: CommandCenterGrid (via HomeAttentionQueue + card counts),
// ReportsRoom (drives refetch), Home briefing (deferred).

import React from 'react';

export const HQ_EVENTS = {
  LEAD_STAGE_CHANGE:   'hq:lead-stage-change',
  APPOINTMENT_UPDATED: 'hq:appointment-updated',
  CONSENT_CHANGED:     'hq:consent-changed',
  STATEMENT_IMPORTED:  'hq:statement-imported',
  AUDIT_EVENT:         'hq:audit-event',
};

export function emitHqEvent(kind, detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent(kind, { detail }));
  } catch { /* SSR / unavailable */ }
}

// Subscribe to a list of hq event kinds; call `handler(kind, detail)`
// when any of them fire. Returns a cleanup fn — pass to useEffect.
export function useHqEvents(kinds, handler) {
  const kindsKey = React.useMemo(() => kinds.join('|'), [kinds]);
  const handlerRef = React.useRef(handler);
  React.useEffect(() => { handlerRef.current = handler; }, [handler]);

  React.useEffect(() => {
    const list = kindsKey.split('|').filter(Boolean);
    const onEvt = (kind) => (e) => {
      try { handlerRef.current?.(kind, e?.detail || {}); } catch {}
    };
    const listeners = list.map(k => ({ kind: k, fn: onEvt(k) }));
    for (const { kind, fn } of listeners) window.addEventListener(kind, fn);
    return () => {
      for (const { kind, fn } of listeners) window.removeEventListener(kind, fn);
    };
  }, [kindsKey]);
}
